import React from 'react'
import { useNavigate } from 'react-router-dom'

// Load Chakra components
import { Box, Button, Image, Stack, Heading } from '@chakra-ui/react'
import LotteryBall from 'component/Lottery/LotteryBall'
import { useSelector } from 'react-redux'

// Load text component
import Text from '../Text/Text'

// Load hooks
import { useLotteriesCombinedById } from '../../../hooks/queries/useLotteries'
import { useGameQuery } from 'hooks/queries/useGames'
import useContentful from 'hooks/useContentful'

// Load helpers
import { translateDrawSchedule } from 'helper/translations'
import { useLanguageContext } from 'component/Translations/Translations'
import { getCdnUrl } from '../../../helper/cdn'
import { toSlug } from 'helper/string'
import { getLanguageISO } from 'helper/date'

import loto from '../../../img/loto.png'
import mas from '../../../img/mas.png'
import supermas from '../../../img/supermas.png'

const lotteryId = 310
const lotterySlug = 'lotomas'

const PrimaryDraw = ({ getText }) => {
    const language = useSelector((state) => state.user.language)
    const { data: lotteryData } = useLotteriesCombinedById(lotteryId)
    const { data } = useGameQuery(lotterySlug)
    const navigate = useNavigate()

    let draw = data?.previousDraws?.[0] ?? {}
    const lottery = lotteryData?.lottery ?? {}
    const hasNumbers = draw?.numbers?.main?.length > 0
    const drawSchedule = translateDrawSchedule({ getText, lottery })
    const resultDate = new Date(draw?.gmtDrawTime).toLocaleDateString(getLanguageISO(language), {
        dateStyle: 'long'
    })

    const resultURL = `/results/${lottery.id}/${toSlug(lottery?.name ?? '')}/${draw.id}`
    return (
        <Stack
            spacing={4}
            maxW={{ base: '100%', md: '46%' }}
            mt={{ base: 10, md: 0 }}
            textAlign="center"
        >
            <Box>
                <Text
                    scope="global"
                    as="h1"
                    mb="0"
                    tag="sentence.playAndWinEveryDay"
                    text="Play and win every day"
                    textStyle="headingFontFamily"
                />
                <Text
                    scope="global"
                    tag="sentence.winMillionsEveryDayPartner"
                    text="Win millions every day on the {partner} lottery site"
                    data={{ partner: 'Leidsa' }}
                    textStyle="headingFontFamily"
                />
            </Box>

            {typeof lottery.logo === 'string' && (
                <Box display="flex" justifyContent="center">
                    <Image
                        maxH="70px"
                        width="auto"
                        objectFit="cover"
                        ignoreFallback={true}
                        alt={`lotomas-logo`}
                        src={getCdnUrl(lottery.logo)}
                    />
                </Box>
            )}
            {hasNumbers && (
                <Box display="flex" width="100%" justifyContent="center" flexWrap="wrap">
                    <>
                        {draw.numbers.main.map((ball) => (
                            <LotteryBall
                                key={`board_${ball}`}
                                number={ball}
                                className="notranslate"
                                ballType="selected"
                                m="2px"
                                size="lg"
                            />
                        ))}
                        {draw?.extraNumbers?.bonus &&
                            draw.extraNumbers.bonus.map((ball, index) => (
                                <LotteryBall
                                    key={`board_${ball}`}
                                    number={ball}
                                    ballType={`bonus${index + 1}`}
                                    className="notranslate"
                                    m="2px"
                                    size="lg"
                                />
                            ))}
                    </>
                </Box>
            )}
            {!hasNumbers && (
                <Text
                    scope="global"
                    tag="resultStatusDescription.pendingResult"
                    text="The results have not been drawn yet. Please check back after the draw."
                />
            )}
            <Box>
                {draw?.gmtDrawTime && (
                    <Text
                        scope="global"
                        tag="sentenceResult.resultsFor"
                        text="Results for {date}"
                        mb={1}
                        data={{ date: resultDate }}
                    />
                )}
                {drawSchedule.drawsAt && (
                    <Text
                        tag={drawSchedule.drawsAt.key}
                        text={drawSchedule.drawsAt.text}
                        data={{
                            drawTime: drawSchedule.drawTime,
                            drawDays: drawSchedule.drawDays
                        }}
                        textStyle="headingFontFamily"
                    />
                )}
            </Box>
            <Box display="flex" justifyContent="space-between">
                <Button width="48%" colorScheme="buttonSecondary" onClick={() => navigate(`play/${data?.url}`)}>
                    {getText('global.button.playNow', 'Play Now')}
                </Button>
                <Button
                    width="48%"
                    onClick={() => navigate(resultURL)}
                >
                    <Text
                        tag="button.moreResults"
                        fontSize="inherit"
                        color="inherit"
                        text="More Results"
                    />
                </Button>
            </Box>
        </Stack>
    )
}

const ColumnHero = () => {
    const { getText } = useLanguageContext()
    const { data: lottoContent } = useContentful({ model: 'nextLotteryDraw', field: 'lotteryName', value: 'Loto' })
    const fields = lottoContent?.[0]?.fields ?? {}

    return (
        <Box
            display="flex"
            flexDirection={{ base: 'column-reverse', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ base: 'center', md: 'flex-start' }}
        >
            <PrimaryDraw getText={getText} />

            <Stack spacing={2} maxW={{ base: '100%', md: '46%' }} textAlign="center">
                <Text
                    scope="global"
                    tag="sentence.forNextDraw"
                    text="For the next draw"
                    as="h1"
                    mb="2"
                    textStyle="headingFontFamily"
                    textTransform="upperCase"
                />

                <Box>
                    <Heading fontSize={{ base: '8xl', lg: '9xl' }} lineHeight="85px">
                        {fields.drawTotal || ''}
                    </Heading>
                    <Text
                        textStyle="headingFontFamily"
                        textTransform="upperCase"
                        fontSize={{ base: '6xl', lg: '7xl' }}
                        scope="global"
                        tag="text.millions"
                        text="Millions"
                    />
                </Box>

                <Box
                    display="grid"
                    gridTemplateColumns={{ base: 'repeat(5, 1fr)', lg: 'repeat(5, 90px)' }}
                    alignItems="flex-start"
                    justifyContent="center"
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Text
                            mb={4}
                            fontSize={{ base: '2xl', lg: '4xl' }}
                            textStyle="lotomasTotalText"
                        >
                            {fields.loto || ''}
                        </Text>

                        <Image src={loto} alt="loto logo" />
                        <Text
                            as="span"
                            scope="global"
                            tag="sentence.accumlatedMillions"
                            text="ACCUMULATED MILLIONS LOTTO"
                            textStyle="bannerSpanText"
                        />
                    </Box>

                    <Text fontSize="2xl" fontWeight="bold">
                        +
                    </Text>

                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Text
                            mb={4}
                            fontSize={{ base: '2xl', lg: '4xl' }}
                            textStyle="lotomasTotalText"
                        >
                            {fields.lotomas || ''}
                        </Text>
                        <Image src={mas} alt="mas logo" />
                        <Text
                            as="span"
                            scope="global"
                            tag="sentence.millionsFixedLotomas"
                            text="MILLIONS FIXED LOTOMAS"
                            textStyle="bannerSpanText"

                        />
                    </Box>

                    <Text fontSize="2xl" fontWeight="bold">
                        +
                    </Text>

                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                    >
                        <Text
                            mb={4}
                            fontSize={{ base: '2xl', lg: '4xl' }}
                            textStyle="lotomasTotalText"
                        >
                            {fields.supermas || ''}
                        </Text>
                        <Image src={supermas} alt="supermas logo" />
                        <Text
                            as="span"
                            scope="global"
                            tag="sentence.supermasFixedMillions"
                            text="SUPERMAS FIXED MILLIONS"
                            textStyle="bannerSpanText"
                        />
                    </Box>
                </Box>
            </Stack>
        </Box>
    )
}

export default ColumnHero
